import { ANALYTICS_CONFIG } from '../config/analytics-config.js';
import { getIdToken } from './firebase-auth.js';

async function metaRequest(path, options, retryAuth) {
  const token = await getIdToken(!!retryAuth);
  const method = (options && options.method) || 'GET';
  const url = new URL(ANALYTICS_CONFIG.apiBase.replace(/\/$/, '') + '/api/v2/meta/analytics/' + path);
  Object.entries((options && options.params) || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    url.searchParams.set(key, String(value));
  });

  const response = await fetch(url.toString(), {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token
    },
    body: method === 'GET' ? undefined : JSON.stringify((options && options.body) || {})
  });

  if (response.status === 401 && !retryAuth) return metaRequest(path, options, true);
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      (payload && (payload.message || (payload.error && payload.error.message) || payload.error)) ||
      'No se pudo consultar Meta.';
    const error = new Error(String(message));
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload || {};
}

function metaGet(path, params, retryAuth) {
  return metaRequest(path, { method: 'GET', params }, retryAuth);
}

export function fetchMetaStatus() {
  return metaGet('status');
}

export function fetchMetaCampaigns(params) {
  return metaGet('campaigns', { limit: 100, ...(params || {}) });
}

export function fetchMetaAdSets(params) {
  return metaGet('adsets', { limit: 100, ...(params || {}) });
}

export function fetchMetaAds(params) {
  return metaGet('ads', { limit: 100, ...(params || {}) });
}

export function fetchMetaInsights(params) {
  return metaGet('insights', { limit: 100, ...(params || {}) });
}

export function fetchMetaAnalysis(input) {
  return metaRequest('analysis', { method: 'POST', body: input || {} });
}

/** Vista puntual de un anuncio real; no se consulta la biblioteca en bloque. */
export function fetchMetaAdPreview(adId) {
  return metaGet('ad-preview/' + encodeURIComponent(String(adId)));
}

/** Solicita a Gemini propuestas en borrador. No publica ni cambia campañas. */
export function fetchMetaCreativeDraft(input) {
  return metaRequest('creative-draft', { method: 'POST', body: input });
}

export function insightActionValue(insight, acceptedTypes) {
  const accepted = new Set(acceptedTypes || []);
  return ((insight && insight.actions) || []).reduce((sum, row) => {
    if (!row || !accepted.has(String(row.actionType || ''))) return sum;
    const value = Number(row.value || 0);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
}

export function insightLeadCount(insight) {
  // Estas acciones pueden representar la misma conversión; no sumarlas entre sí.
  // Meta puede reportar conjuntos distintos según configuración y objetivo.
  for (const type of ['lead', 'onsite_conversion.lead_grouped', 'offsite_conversion.fb_pixel_lead']) {
    const rows = (insight && insight.actions) || [];
    if (rows.some((item) => item.actionType === type)) {
      return insightActionValue(insight, [type]);
    }
  }
  return 0;
}

export function aggregateInsights(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const sums = list.reduce(
    (acc, row) => {
      acc.spend += Number(row && row.spend) || 0;
      acc.impressions += Number(row && row.impressions) || 0;
      acc.reach += Number(row && row.reach) || 0;
      acc.clicks += Number(row && row.clicks) || 0;
      acc.leads += insightLeadCount(row);
      return acc;
    },
    { spend: 0, impressions: 0, reach: 0, clicks: 0, leads: 0 }
  );
  return {
    ...sums,
    ctr: sums.impressions > 0 ? (sums.clicks / sums.impressions) * 100 : 0,
    cpc: sums.clicks > 0 ? sums.spend / sums.clicks : 0,
    cpl: sums.leads > 0 ? sums.spend / sums.leads : 0,
    frequency: sums.reach > 0 ? sums.impressions / sums.reach : 0
  };
}

export async function fetchCampaignPerformance(campaigns, range, onProgress) {
  const list = Array.isArray(campaigns) ? campaigns : [];
  const output = [];
  const chunkSize = 5;
  for (let i = 0; i < list.length; i += chunkSize) {
    const chunk = list.slice(i, i + chunkSize);
    const rows = await Promise.all(
      chunk.map(async (campaign) => {
        try {
          const result = await fetchMetaInsights({
            campaignId: campaign.id,
            since: range.dateFrom,
            until: range.dateTo
          });
          const metrics = aggregateInsights((result && result.data) || []);
          return { ...campaign, metrics, error: '' };
        } catch (error) {
          return {
            ...campaign,
            metrics: aggregateInsights([]),
            error: (error && error.message) || 'Error Meta'
          };
        }
      })
    );
    output.push(...rows);
    if (typeof onProgress === 'function') onProgress([...output]);
  }
  return output;
}


/**
 * Lee todas las páginas de una colección Meta. Un cursor \`after\` sin
 * \`hasNext\` NO significa que exista una página siguiente.
 * Ante una extracción parcial se falla explícitamente para evitar KPI falsos.
 */
export async function fetchAllMetaPages(fetchPage, params = {}, maxPages = 100, onPage) {
  const rows = [];
  const seen = new Set();
  let after;
  for (let pageNumber = 0; pageNumber < maxPages; pageNumber += 1) {
    const result = await fetchPage({ ...params, ...(after ? { after } : {}) });
    if (!result || !Array.isArray(result.data)) {
      throw new Error('Meta devolvió un formato inesperado de resultados.');
    }
    if (typeof onPage === 'function') onPage(result);
    rows.push(...result.data);
    if (!result.paging || !result.paging.hasNext) return rows;

    const next = result.paging.after;
    if (!next || seen.has(next)) {
      throw new Error('Meta no entregó un cursor válido para continuar la extracción.');
    }
    seen.add(next);
    after = next;
  }
  throw new Error('La extracción de Meta superó el máximo de páginas. No se muestran datos parciales.');
}

/**
 * Un único snapshot por período: catálogos + tres consultas de Insights
 * de cuenta (campaña, conjunto y anuncio), paginadas cuando sea necesario.
 * Los cambios de filtro NO invocan Meta.
 */
/**
 * Lee insights por nivel sin pedir catálogos /adsets y /ads (dos llamadas
 * adicionales que estaban devolviendo 429). Las identidades se construyen
 * con campaign_id, adset_id y ad_id del propio desglose de Insights.
 * Conjunto/anuncio pueden quedar temporalmente indisponibles; nunca
 * se representan como cero si el proveedor no entregó ese desglose.
 */
export async function fetchMetaSnapshot(range, options = {}) {
  const read = options.fetchInsights || fetchMetaInsights;
  const byLevel = { campaign: [], adset: [], ad: [] };
  const unavailableLevels = [];
  let stale = false;
  let cachedAt = null;

  for (const level of ['campaign', 'adset', 'ad']) {
    try {
      byLevel[level] = await fetchAllMetaPages(read, {
        level, since: range.dateFrom, until: range.dateTo
      }, 100, (result) => {
        if (result.stale) {
          stale = true;
          if (result.cachedAt) cachedAt = result.cachedAt;
        }
      });
    } catch (error) {
      if (level === 'campaign') throw error; // Sin campaña no existen KPI confiables.
      unavailableLevels.push(level);
    }
  }

  const campaigns = new Map();
  const adsets = new Map();
  const ads = new Map();
  for (const row of byLevel.campaign) {
    const id = String(row.campaignId || '');
    if (id && !campaigns.has(id)) {
      campaigns.set(id, {
        id, name: row.campaignName || id,
        status: 'WITH_RESULTS', effectiveStatus: 'WITH_RESULTS'
      });
    }
  }
  for (const row of byLevel.adset) {
    const id = String(row.adsetId || '');
    if (id && !adsets.has(id)) {
      adsets.set(id, {
        id, name: row.adsetName || id, campaignId: row.campaignId
      });
    }
  }
  for (const row of byLevel.ad) {
    const id = String(row.adId || '');
    if (id && !ads.has(id)) {
      ads.set(id, {
        id, name: row.adName || id, adsetId: row.adsetId,
        campaignId: row.campaignId
      });
    }
  }
  return {
    campaigns: [...campaigns.values()],
    adsets: [...adsets.values()],
    ads: [...ads.values()],
    byLevel, unavailableLevels, stale, cachedAt
  };
}

export function selectInsightsForFilters(byLevel, filters) {
  const { campaign = 'all', adset = 'all', ad = 'all' } = filters || {};
  const level = ad !== 'all' ? 'ad' : adset !== 'all' ? 'adset' : 'campaign';
  const rows = Array.isArray(byLevel && byLevel[level]) ? byLevel[level] : [];
  return rows.filter((row) => {
    if (campaign !== 'all' && String(row.campaignId || '') !== String(campaign)) return false;
    if (adset !== 'all' && String(row.adsetId || '') !== String(adset)) return false;
    if (ad !== 'all' && String(row.adId || '') !== String(ad)) return false;
    return true;
  });
}

export function campaignPerformanceFromInsights(campaigns, byLevel) {
  const grouped = new Map();
  for (const insight of (byLevel && byLevel.campaign) || []) {
    const key = String(insight.campaignId || '');
    if (!key) continue;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(insight);
  }
  return (campaigns || []).map((campaign) => ({
    ...campaign,
    metrics: aggregateInsights(grouped.get(String(campaign.id)) || []),
    error: ''
  }));
}
